import * as React from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Head from 'next/head';
import dynamic from 'next/dynamic';

import { Props as EditorProps } from 'components/Editor/Editor';
import styles from './EssentialPages.module.css';
import { ReduxState } from 'ducks';
import { selectPMPEnabled } from 'ducks/server/settings';
import { selectBookingWidgetBannerImageUrls } from 'lib/util/bookingWidgetDesign';
import { selectTopPageBannerImageUrl } from 'lib/util/privateMarketplace';

const Editor = dynamic<EditorProps>(
  () => import('components/Editor/Editor').then((mod) => mod.Editor) as any,
  {
    ssr: false,
  }
);

export const AboutUs = (): any => {
  const { t, i18n } = useTranslation();
  const settings = useSelector((state: ReduxState) => state.server.settings.all);

  const pmpEnabled = useSelector(selectPMPEnabled);

  let ogpImageUrl = '';
  if (pmpEnabled) {
    ogpImageUrl = useSelector(selectTopPageBannerImageUrl);
  } else {
    const bannerImageUrls = useSelector(selectBookingWidgetBannerImageUrls);
    if (bannerImageUrls.length > 0) {
      ogpImageUrl = bannerImageUrls[0];
    }
  }

  const aboutUsSettings = settings?.essential_pages?.about_us_settings;

  const title = aboutUsSettings?.title || '';
  const keyVisual = aboutUsSettings?.key_visual || ogpImageUrl;
  const googleMapTag = aboutUsSettings?.google_map_tag || '';
  const operatingHoursRules = settings?.operating_hours_rules || [];
  const message = (aboutUsSettings?.messages || []).find(
    (message) => message?.content_language === i18n.language.toUpperCase().replace('-', '_')
  );

  const parsedMessageBody = message?.body ? JSON.parse(message?.body) : null;

  return (
    <>
      <Head>
        {title && (
          <>
            <title>{title}</title>
            <meta property="og:title" content={title} />
          </>
        )}
        {keyVisual && <meta property="og:image" content={keyVisual} />}
      </Head>
      <div className={clsx(styles['base-wrap'], styles['color-txt'], styles['color-bg'])}>
        {false && <pre>{JSON.stringify(aboutUsSettings, undefined, 2)}</pre>}
        <main className={clsx(styles['base-main styles.paddingTop32-sp'])}>
          <div className={clsx(styles['base-info'])}>
            {keyVisual && (
              <div className={clsx(styles['base-info__visual'])}>
                <img src={keyVisual} />
                <h1>{title}</h1>
              </div>
            )}

            {(parsedMessageBody?.blocks?.length ?? []) > 0 && (
              <div className={clsx(styles['base-info__editor'])}>
                <Editor data={parsedMessageBody} minHeight={0} />
              </div>
            )}

            <section className={clsx(styles['base-info__frame'])}>
              <p className={clsx(styles['base-info__frame__ttl'])}>{t('Company Profile')}</p>
              <ul className={clsx(styles['base-info__frame__list'])}>
                <li>
                  <p className={'maincolor-txt'}>{t('Name')}</p>
                  <p>{settings?.supplier_name}</p>
                </li>
                <li>
                  <p className={'maincolor-txt'}>{t('Address')}</p>
                  <p>{settings?.supplier_address}</p>
                </li>
                <li>
                  <p className={'maincolor-txt'}>{t('Phone Number')}</p>
                  <p>{settings?.supplier_phone}</p>
                </li>
                <li>
                  <p className={'maincolor-txt'}>{t('Email')}</p>
                  <p>{settings?.supplier_email}</p>
                </li>
                <li>
                  <p className={'maincolor-txt'}>{t('Open Hours')}</p>
                  <p>
                    {operatingHoursRules.map((rule) => {
                      return (
                        <>
                          {rule.start_time_local} - {rule.end_time_local}
                          <br />
                        </>
                      );
                    })}
                  </p>
                </li>
                {(aboutUsSettings?.other_company_profiles || []).map((profile, idx) => {
                  return (
                    <li key={idx}>
                      <p className={'maincolor-txt'}>{profile.key}</p>
                      <p>{profile.value}</p>
                    </li>
                  );
                })}
              </ul>

              {googleMapTag && (
                <>
                  <p className={clsx(styles['base-info__frame__ttl'])}>{t('Access')}</p>
                  <div
                    className={clsx(styles['base-info__frame__map'])}
                    dangerouslySetInnerHTML={{ __html: googleMapTag }}
                  />
                </>
              )}
            </section>
          </div>
        </main>
      </div>
    </>
  );
};
